import { motion, useReducedMotion } from "motion/react";

/**
 * The monogram, as a placeholder for a supplied mark.
 *
 * **To swap in a real logo:** replace the contents of `<Mark>` with an
 * `<img src="/logo.svg" alt="" />` or an inline `<svg>`. Nothing else needs
 * to change — the link, the accessible name, the sizing and the hover
 * behaviour all live out here and are independent of what is drawn inside.
 *
 * Two details that are easy to get wrong and expensive to fix later:
 *
 *   The accessible name is "Iftikhar Ali — home", not "IA". A screen-reader
 *   user hearing "eye ay, link" learns nothing. When a real mark arrives its
 *   `alt` must be empty, because this label already names the link — an image
 *   with alt text *inside* a labelled link gets announced twice.
 *
 *   The glyphs are `aria-hidden` and the label is visually hidden text. That
 *   ordering means the monogram is decoration on top of a real link rather
 *   than a link whose name happens to be two letters.
 */

function Mark() {
  return (
    <span
      aria-hidden="true"
      className="font-display text-[1.35rem] leading-none font-semibold tracking-[-0.04em] text-text transition-colors duration-500 group-hover:text-accent"
      style={{ fontVariationSettings: '"SOFT" 40, "opsz" 72' }}
    >
      IA
    </span>
  );
}

export function Logo() {
  const reduced = useReducedMotion() ?? false;

  return (
    <span className="group relative inline-flex items-center">
      {/* The bloom. Sits behind the glyphs, blurred, and only on hover — a
          permanently glowing logo is a notification badge. */}
      <motion.span
        aria-hidden="true"
        initial={false}
        whileHover={reduced ? undefined : { opacity: 1, scale: 1 }}
        className="pointer-events-none absolute -inset-3 rounded-full bg-glow/25 opacity-0 blur-lg transition-opacity duration-500 group-hover:opacity-100"
      />
      <span className="relative flex items-baseline gap-1.5">
        <Mark />
        {/* The rule grows from nothing on hover. It is the whole animation:
            one axis, 500ms, no bounce. A monogram that springs is a toy. */}
        <span
          aria-hidden="true"
          className="mb-0.5 h-px w-0 self-end bg-accent transition-[width] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:w-6"
        />
      </span>
    </span>
  );
}
